'use client';
import { LeftOutlined, RightOutlined } from "@ant-design/icons";
import { MouseEventHandler, useEffect, useState, useRef } from "react";
import ImageGallery, { ReactImageGalleryItem } from "react-image-gallery";
import { handleImgUrl, preloadImage } from "@/utils/tools";
import { Image } from "antd";
import type { ImageProps } from "antd/es/image";
import ImageWithSkeleton from "@/app/components/common/ImageWithSkeleton";
import useMobileDetect from "@/hooks/useMobileDetect";

import "react-image-gallery/styles/css/image-gallery.css";
import "./style/ImagesGallery.css";

const imgs = [
  {
    original: "https://picsum.photos/616",
    thumbnail: "https://picsum.photos/66",
  },
  {
    original: "https://picsum.photos/616",
    thumbnail: "https://picsum.photos/66",
  },
  {
    original: "https://picsum.photos/616",
    thumbnail: "https://picsum.photos/66",
  }
] as readonly ReactImageGalleryItem[];

export type ImagesGalleryProps = {
  images?: {
    whiteBackPic: string[];
    scenePic: string[];
    detailPic: string[];
    measurementPic: string[];
  };
  altStr: string;
  stock: boolean;
  isUnavailable: boolean;
  isFirstMobile?: boolean
};

const navStyle = {
  backgroundColor: "rgba(0, 0, 0, 0.8)",
  width: 32,
  height: 48,
  display: "flex",
  justifyContent: "center",
  position: "absolute",
  zIndex: 4,
  top: '50%',
  marginTop: -24,
  background: 'rgba(243,242,236, 0.7)',
  // borderRadius: 4,
} as React.CSSProperties;

export const ImagesGallery: React.FC<ImagesGalleryProps> = ({
  images = imgs,
  altStr,
  stock,
  isUnavailable,
  isFirstMobile = false
}) => {
  const [index, setIndex] = useState(0)
  const isMobile = useMobileDetect(isFirstMobile);
  const [showFullPreviewImg, setShowFullPreviewImg] = useState(false)
  // 使用 useRef 存储图片的引用
  const imageRefs = useRef<(HTMLImageElement | null)[]>([]);
  const handlePreview = () => {
    // 触发第一张图片的预览
    if (imageRefs.current[0]) {
      imageRefs.current[0].click();
    }
  };

  const pics: (ReactImageGalleryItem & { index: number, original_1: string })[] = []
  let i = 0
  Object.keys(images).forEach((key: string) => {
    // @ts-ignore
    images[key as string] && images[key as string].forEach((img: string) => {
      pics.push({
        original_1: process.env.NEXT_PUBLIC_IMAGE_URL + img,
        original: handleImgUrl(process.env.NEXT_PUBLIC_IMAGE_URL + img, '1200x1200'),
        thumbnail: handleImgUrl(process.env.NEXT_PUBLIC_IMAGE_URL + img, '200x200'),
        originalAlt: altStr,
        thumbnailAlt: altStr,
        index: i++
      })
    });
  });

  const loadBatch = () => {
    setTimeout(() => {
      pics.forEach((pic, index) => {
        if (index > 0) {
          const img = new globalThis.Image();
          img.src = pic.original
        }
      })
    }, 1000)
  }
  // 图片预加载
  useEffect(() => {
    globalThis.addEventListener('load', loadBatch);
    // pics.forEach((pic) => preloadImage(pic.original))
    // 页面加载完成后立即加载第一批
    return () => {
      globalThis.removeEventListener('load', loadBatch);
    }
  }, [])


  const handleImageClick = (event: any, item: ReactImageGalleryItem & { index: number }) => {
    setShowFullPreviewImg(true)
    setTimeout(() => {
      if (imageRefs.current[index]) {
        //ts-ignore
        (imageRefs.current[index].childNodes[0] as HTMLElement).click()
      }
    }, 200)
  };

  return (<>
    {
      isMobile ?
        <div className="md:hidden m-[-12px] h5-ImageGallery relative">
          <ImageGallery
            items={pics as any}
            lazyLoad={true}
            showNav={false}
            showFullscreenButton={false}
            showPlayButton={false}
            showThumbnails={false}
            showBullets={true}
            slideOnThumbnailOver
            infinite={false}
            onSlide={(index: number) => {
              setIndex(index);
            }}
            renderItem={(item) => {
              return (
                <div
                  className="image-gallery-image"
                  onClick={(e) => handleImageClick(e, item as ReactImageGalleryItem & { index: number })}
                >
                  <img
                    src={item.original}
                    alt={item.originalAlt}
                  />
                  {/* <ImageWithSkeleton
                    src={item.original}
                    width={247}
                    height={247}
                    alt={item.originalAlt || ''}
                  /> */}
                </div>
              );
            }}
          />
          {
            (stock || isUnavailable) && <div className="absolute text-white text-2xl font-medium w-176 h-60 rounded-xs flex z-10
          items-center justify-center top-1/2 right-1/2 mr-[-88px] mt-[-30px] leading-28" style={{ background: "rgba(64, 44, 34, 0.5)" }}>
              {stock ? "Out of Stock" : "Unavailable"}
            </div>
          }
        </div>
        :
        <div className="hidden md:block relative">
          <div>
            <ImageGallery
              items={pics as any}
              lazyLoad={true}
              showNav={pics.length > 1}
              showFullscreenButton={false}
              showPlayButton={false}
              slideOnThumbnailOver
              infinite={false}
              thumbnailPosition="left"
              onSlide={(index: number) => {
                setIndex(index);
              }}
              renderLeftNav={(
                onClick: MouseEventHandler<HTMLSpanElement> | undefined,
                disabled: boolean | undefined
              ) => {
                return (
                  <div
                    style={{ ...navStyle, left: 0 }}
                    className={`${index === 0 ? "opacity-30" : "cursor-pointer"}`}
                    onClick={(event) => {
                      index > 0 && onClick && onClick(event);
                    }}
                  >
                    <iconpark-icon
                      icon-id="left"
                      size="2.6rem"
                      name="left"
                      color={"#402C22"}
                    />
                  </div>
                );
              }}
              renderRightNav={(
                onClick: MouseEventHandler<HTMLSpanElement> | undefined,
                disabled: boolean | undefined
              ) => {
                return (<>
                  <div
                    style={{ ...navStyle, right: 0 }}
                    className={`${index >= pics.length - 1 ? "opacity-30" : "cursor-pointer"}`}
                    onClick={(event) => {
                      index <= pics.length - 1 && onClick && onClick(event);
                    }}
                  >
                    <iconpark-icon
                      icon-id="right"
                      size="2.6rem"
                      name="right"
                      color={"#402C22"}
                    />
                  </div>
                  {
                    (stock || isUnavailable) && <div className="absolute text-white text-2xl font-medium w-176 h-60 rounded-xs flex z-10
                  items-center justify-center top-1/2 right-1/2 mr-[-88px] mt-[-24px] leading-28" style={{ background: "rgba(64, 44, 34, 0.5)" }}>
                      {stock ? "Out of Stock" : "Unavailable"}
                    </div>
                  }
                </>);
              }}
              renderItem={(item: ReactImageGalleryItem) => {
                return (
                  <div
                    className="image-gallery-image"
                    onClick={(e) => handleImageClick(e, item as ReactImageGalleryItem & { index: number })}
                  >
                    <img
                      src={item.original}
                      alt={item.originalAlt}
                      onClick={(e) => handleImageClick(e, item as ReactImageGalleryItem & { index: number })}
                    />
                    {/* <ImageWithSkeleton
                      src={item.original}
                      width={247}
                      height={247}
                      alt={item.originalAlt || ''}
                    /> */}
                  </div>
                );
              }}
            />
          </div>
        </div>
    }

    {
      showFullPreviewImg ?
        <div className="hidden">
          <Image.PreviewGroup>
            {pics.map((item, index) => (
              <div key={index} ref={(ref: any) => {
                imageRefs.current[index] = ref
              }}>
                <Image
                  width={200}
                  loading="lazy"
                  src={item.original_1}
                  alt={item.originalAlt}
                  // style={{ display: "none" }} // 隐藏图片
                  preview={{
                    visible: false, // 默认不显示预览
                    getContainer: false, // 避免预览框被隐藏
                  }}
                />
              </div>
            ))}
          </Image.PreviewGroup>
        </div>
        :
        null
    }
  </>
  )
};
